import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { useApiError } from '@/hooks/useApiError';
import { loginSchema, LoginFormData } from '@/types/forms';
import { SavedSessionsList } from '../components/SavedSessionsList';
import { AuthLayout } from '../components/AuthLayout';
import { LoginBrandPanel } from '../components/LoginBrandPanel';
import { Icon } from '@/shared/icons';

/** Escurece um hex #rrggbb em `pct`%. Fallback: retorna o valor original. */
const darken = (hex: string, pct: number): string => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.floor((num >> 16) * (1 - pct / 100)));
  const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - pct / 100)));
  const b = Math.max(0, Math.floor((num & 0xff) * (1 - pct / 100)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

export const Login: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const { login, savedSessions, loginWithSavedSession, removeSession } = useAuth();
  const { app } = useApp();
  const { handleApiError } = useApiError();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';
  const primaryColor = app?.themeOverrides?.colors?.primary || '#8b5cf6';

  // Auth = tema ESCURO fixo (decisão do dono 2026-07-20). Não alteramos o
  // mecanismo do AuthLayout (ThemeProvider forceMode, compartilhado com
  // Register/Reset); estabelecemos a paleta dark via override de --theme-*
  // escopado a esta página, mantendo a cor primária white-label do app.
  const darkTheme = {
    '--theme-page-background': '#0e1524',
    '--theme-text': '#f1f5f9',
    '--theme-muted-text': '#9aa6b8',
    '--theme-card-background': '#161f30',
    '--theme-card-text': '#e2e8f0',
    '--theme-card-muted': '#6b7890',
    '--theme-card-border': 'rgba(255,255,255,0.09)',
    '--theme-border': 'rgba(255,255,255,0.09)',
    '--theme-input-background': '#161f30',
    '--theme-input-text': '#f1f5f9',
    '--theme-input-border': 'rgba(255,255,255,0.14)',
    '--theme-input-focus': primaryColor,
    '--theme-surface-sunken': '#111a29',
    '--theme-primary': primaryColor,
    '--theme-button-primary': primaryColor,
    '--theme-button-primary-text': '#ffffff',
    '--theme-button-primary-hover': darken(primaryColor, 12),
    '--theme-button-ghost-hover': 'rgba(255,255,255,0.05)',
    '--theme-error-color': '#f87171',
    '--ring-color': `${primaryColor}29`,
    backgroundColor: '#0e1524',
  } as React.CSSProperties;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
    setFocus,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      remember: false,
    },
  });

  // Preencher email se vier do registro e dar foco no password
  React.useEffect(() => {
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setValue('email', stateEmail);
      setTimeout(() => {
        setFocus('password');
      }, 100);
    }
  }, [location.state, setValue, setFocus]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await login({
        email: data.email,
        password: data.password,
        rememberMe: data.remember,
      });

      toast.success(`Bem-vindo, ${response.user.firstName}!`);
      navigate(from, { replace: true });
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  const handleSessionLogin = async (session: any) => {
    try {
      await loginWithSavedSession(session);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error logging in with saved session:', error);

      toast.error('Sessão expirada. Por favor, digite sua senha para continuar.');
      setValue('email', session.email);
      removeSession(session.id);

      setTimeout(() => {
        setFocus('password');
      }, 100);
    }
  };

  const handleSessionDelete = (sessionId: string) => {
    removeSession(sessionId);
  };

  return (
    <AuthLayout>
      <div
        className="min-h-screen min-[860px]:grid min-[860px]:h-screen min-[860px]:grid-cols-2 min-[860px]:overflow-hidden"
        style={darkTheme}
        data-testid="login-page"
      >
        {/* Painel de marca (coluna esquerda, fixo, some no mobile) */}
        <LoginBrandPanel app={app} primaryColor={primaryColor} />

        {/* Coluna do formulário (rola) */}
        <main
          className="flex min-h-screen items-start justify-center overflow-y-auto px-6 pb-12 pt-14 min-[860px]:h-screen min-[860px]:min-h-0 min-[860px]:items-center min-[860px]:py-10"
          style={{ backgroundColor: 'var(--theme-page-background)' }}
        >
          <div className="w-full max-w-[400px]">
            {/* Logo mobile */}
            <div className="mb-7 flex items-center gap-2.5 min-[860px]:hidden">
              {app?.logo ? (
                <img src={app.logo} alt={app.name || 'Logo'} className="h-9 w-auto" />
              ) : (
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-[9px] text-base font-black text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {app?.name?.charAt(0)?.toUpperCase() || 'T'}
                </span>
              )}
              <span className="text-lg font-extrabold tracking-tight" style={{ color: 'var(--theme-text)' }}>
                {app?.name || 'TROIA'}
              </span>
            </div>

            {/* Título e subtítulo */}
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--theme-text)' }}>
              Bem-vindo de volta
            </h1>
            <p className="mb-6 mt-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Faça login para continuar ou{' '}
              <Link
                to="/register"
                className="font-semibold hover:underline"
                style={{ color: primaryColor }}
                data-testid="register-link"
              >
                crie uma conta
              </Link>
            </p>

            {/* Sessões salvas */}
            <SavedSessionsList
              sessions={savedSessions}
              onSessionLogin={handleSessionLogin}
              onSessionDelete={handleSessionDelete}
              primaryColor={primaryColor}
            />

            {/* Formulário */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="login-form">
              <FormField label="E-mail" error={errors.email?.message} required>
                <Input
                  {...register('email', {
                    setValueAs: (value) => value,
                  })}
                  type="email"
                  autoComplete="email"
                  placeholder="voce@empresa.com"
                  icon="mail"
                  error={errors.email?.message}
                  data-testid="email-input"
                />
              </FormField>

              <FormField label="Senha" error={errors.password?.message} required>
                <div className="relative">
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Sua senha"
                    icon="lock"
                    error={errors.password?.message}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ color: 'var(--theme-muted-text)' }}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    data-testid="toggle-password"
                  >
                    <Icon name={showPassword ? 'eye-off' : 'eye'} className="h-4 w-4" />
                  </button>
                </div>
              </FormField>

              <div className="flex items-center justify-between pt-1">
                <label
                  htmlFor="remember"
                  className="flex cursor-pointer items-center gap-2 text-[13px]"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  <input
                    {...register('remember')}
                    id="remember"
                    type="checkbox"
                    className="h-[18px] w-[18px] rounded"
                    style={{ accentColor: primaryColor }}
                    data-testid="remember-checkbox"
                  />
                  Manter conectado
                </label>

                <Link
                  to="/reset-password"
                  className="text-[13px] font-semibold hover:underline"
                  style={{ color: primaryColor }}
                  data-testid="forgot-password-link"
                >
                  Esqueci a senha
                </Link>
              </div>

              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={isSubmitting}
                data-testid="login-submit"
              >
                Entrar
              </Button>
            </form>

            {/* Rodapé legal */}
            <p className="mt-6 text-center text-[12.5px] leading-relaxed" style={{ color: 'var(--theme-card-muted)' }}>
              Ao entrar, você concorda com nossos{' '}
              <a href="#" className="hover:underline" style={{ color: 'var(--theme-muted-text)' }}>
                Termos de Uso
              </a>{' '}
              e{' '}
              <a href="#" className="hover:underline" style={{ color: 'var(--theme-muted-text)' }}>
                Política de Privacidade
              </a>
            </p>
          </div>
        </main>
      </div>
    </AuthLayout>
  );
};
